import React from "react";
import PageTitle from "@Components/PageTitle/PageTitle";
import NotificationsIcon from "@mui/icons-material/Notifications";
import themeCreator from "@Themes/themeCreator";

import { Container, Tabs, Tab, Typography, Button } from "@mui/material";
import { Box } from "@mui/system";
import { useSelector, useDispatch } from "react-redux"; // Import useSelector and useDispatch from react-redux

import { ListingPageContainer } from "../ListingPageTemplate/ListingPageTemplateStyles";
import NotificationTile from "./NotificationTile";
import { DeletePopup } from "@Components/Popup/DeletePopup";
import { useEffect, useState } from "react";
import useGetAll from "@Hooks/useGetAll";
import { NOTIFICATIONS_URL, MARK_ALL_NOTIFICATIONS_READ, DELETE_ALL_NOTIFICATIONS } from "@API/APIRoutes";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import { useNavigate } from "react-router-dom";
import * as DefaultRoutes from "@Routes/DefaultRoutes";
import usePostItem from "@Hooks/usePostItem";
import useGetItem from "@Hooks/useGetItem";
import useDeleteItem from "@Hooks/useDeleteItem";
import axiosInstance from "@API/axios";
import { setSnackbar } from "@Redux/snackBarSlice";
import DelegateRequestDetails from "../DelegateRequestDetails/DelegateRequestDetails";
import {
  formatNotifications,
  formatDate,
} from "@Utils/Validations/HelperFunctions/FormaterFunctions";

export default function Notification() {
  const theme = themeCreator();
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();

  const [tabValue, setTabValue] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showDelegateDetails, setShowDelegateDetails] = useState(false);
  const [selectedDelegateRequest, setSelectedDelegateRequest] = useState(null);
  const [showDeleteAllPopup, setShowDeleteAllPopup] = useState(false);
  const { loading, getAll } = useGetAll();
  const { loading: postLoading, postItem } = usePostItem();
  const { loading: delegateLoading, getItem } = useGetItem();
  const { loading: deleteLoading, deleteItem } = useDeleteItem();
  const navigate = useNavigate();

  const handleClick = (notification) => {
    postItem(
      `${NOTIFICATIONS_URL}/${notification.id}/read`,
      {},
      () => {},
      "",
      "Failed to mark notification as read",
      false
    );
    const updatedNotifications = notifications?.map((notf) => {
      if (notf.id === notification.id) {
        return { ...notf, read: true };
      }
      return notf;
    });
    setNotifications(updatedNotifications);

    // Check if it's a delegation-related notification
    if (
      notification.subject === "DELEGATION REQUEST" ||
      notification.subject === "DELEGATION ACCEPTED" ||
      notification.subject === "DELEGATION REJECTED"
    ) {
      // Navigate to delegation request details page
      if (notification.delegationRequestId) {
        navigate(
          DefaultRoutes.delegateRequestDetail.replace(
            ":delegateId",
            notification.delegationRequestId
          )
        );
      } else {
        // Fallback if no delegationRequestId - navigate to delegation requests list
        if (user.role === "REGULATORY") {
          navigate(DefaultRoutes.regDelegationRequests);
        } else {
          navigate(DefaultRoutes.rdDelegationRequests);
        }
      }
    } else if (
      notification.subject === "SUPPORT REQUEST CREATED" ||
      (notification.supportRequestId && !notification.exitRequestId)
    ) {
      // Navigate to support request detail page
      if (notification.supportRequestId) {
        navigate(
          DefaultRoutes.supportRequestDetail.replace(
            ":requestId",
            notification.supportRequestId
          )
        );
      } else {
        // Fallback: navigate to support provider home if no ID available
        navigate(DefaultRoutes.supportProviderHome);
      }
    } else if (notification.exitRequestId) {
      // For exit request notifications, navigate to exit request info
      navigate(DefaultRoutes.reqInfo.replace(":reqId", notification.exitRequestId));
    } else {
      // Fallback: no valid ID found
      console.warn('Notification has no valid route ID:', notification);
    }
  };

  const handleBackFromDelegateDetails = () => {
    setShowDelegateDetails(false);
    setSelectedDelegateRequest(null);
  };

  const handleMarkAllAsRead = () => {
    postItem(
      MARK_ALL_NOTIFICATIONS_READ,
      {},
      () => {
        const updatedNotifications = notifications?.map((notf) => ({
          ...notf,
          read: true,
        }));
        setNotifications(updatedNotifications);
      },
      "All notifications marked as read",
      "Failed to mark all notifications as read",
      false
    );
  };

  const handleDeleteAll = () => {
    setShowDeleteAllPopup(true);
  };

  const confirmDeleteAll = () => {
    axiosInstance
      .delete(DELETE_ALL_NOTIFICATIONS)
      .then(() => {
        setNotifications([]);
        setShowDeleteAllPopup(false);
        dispatch(
          setSnackbar({
            open: true,
            message: "All notifications deleted",
            error: false,
          })
        );
      })
      .catch((error) => {
        setShowDeleteAllPopup(false);
        dispatch(
          setSnackbar({
            open: true,
            message: "Failed to delete all notifications",
            error: true,
          })
        );
      });
  };

  const handleDeleteNotification = (notificationId) => {
    deleteItem(
      notificationId,
      () => {
        const updatedNotifications = notifications?.filter(
          (notf) => notf.id !== notificationId
        );
        setNotifications(updatedNotifications);
      },
      NOTIFICATIONS_URL,
      "Notification deleted",
      "Failed to delete notification"
    );
  };

  const filteredNotifications =
    tabValue === 0
      ? notifications
      : notifications?.filter((notification) => !notification.read);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    getAll(NOTIFICATIONS_URL).then((response) => {
      console.log("user", user);
      console.log("Fetched notifications:", response);
      const formattedResponse = formatNotifications(response);
      const sortedNotifications = formattedResponse.sort((a, b) => {
        const fixDate = (arr) => {
          const [year, month, day, hour, min, sec, msRaw] = arr;
          const ms = Math.min(Math.max(msRaw || 0, 0), 999); // Clamp ms
          return new Date(
            year,
            month - 1,
            day,
            hour || 0,
            min || 0,
            sec || 0,
            ms
          );
        };

        const dateA = fixDate(a.createdAt);
        const dateB = fixDate(b.createdAt);
        return dateB - dateA;
      });

      setNotifications(sortedNotifications);
      console.log("All notifications (sorted)", sortedNotifications);
    });
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={theme.pageHeight}
      >
        <LoadingComponent />
      </Box>
    );
  }

  return (
    <>
      {showDelegateDetails ? (
        <DelegateRequestDetails
          requestData={selectedDelegateRequest}
          onBack={handleBackFromDelegateDetails}
        />
      ) : (
        <ListingPageContainer className="listingPageContainer">
          <Container
            sx={{ pb: 5, pr: "0px !important", pl: "0px !important" }}
            disableGutters
          >
            <Box className="LabelButtonBox">
              <PageTitle
                title="Notifications"
                PageIcon={<NotificationsIcon color="white" fontSize="large" />}
                iconColor={theme.palette.primary.main}
              ></PageTitle>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: "divider", marginTop: 2 }}>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab label={`All (${notifications?.length})`} />
                <Tab
                  label={`Unread (${notifications?.filter((n) => !n.read).length})`}
                />
              </Tabs>
            </Box>

            {notifications?.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  marginTop: 2,
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleMarkAllAsRead}
                  disabled={notifications?.filter((n) => !n.read).length === 0}
                >
                  Mark All as Read
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDeleteAll}
                >
                  Delete All
                </Button>
              </Box>
            )}
            <Box
              sx={{
                marginTop: 3,
                overflowY: "auto",
                maxHeight: "calc(100vh - 280px)",
                paddingRight: "8px",
              }}
            >
              {filteredNotifications?.length === 0 ? (
                <Typography
                  variant="body1"
                  sx={{
                    textAlign: "center",
                    color: "#757575",
                    marginTop: "20px",
                  }}
                >
                  There are no notifications
                </Typography>
              ) : (
                filteredNotifications?.map((notification) => (
                  <NotificationTile
                    key={notification.id}
                    title={notification.subject}
                    message={notification.message}
                    date={formatDate(notification.createdAt)}
                    isRead={notification.read}
                    theme={theme}
                    onViewFullOrder={() => handleClick(notification)}
                    onDelete={() => handleDeleteNotification(notification.id)}
                  />
                ))
              )}
            </Box>
          </Container>
        </ListingPageContainer>
      )}
      {DeletePopup(
        "all notifications",
        showDeleteAllPopup,
        () => setShowDeleteAllPopup(false),
        confirmDeleteAll,
        null,
        deleteLoading,
        "Delete"
      )}
    </>
  );
}
